import { trackedArray } from '@ember/reactive/collections';
import { scheduleOnce } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';

import { equal, sortBy } from 'webapp/utils/common';

interface BufferedModelType {
	model?: object;
}

interface Options<ModelType> {
	elementSelector: string; // root element, also the one with the data props
	dataIdProperty: string; // must exist on model and as data-<this> parameter on node (lowercase)
	dataSortProperty: string; // must exist on model and as data-<this> parameter on node (lowercase)
	getModel: () => ModelType[];
	onNodeDelete: (node: JQuery<HTMLElement>) => void;
	skipSortByDataSortProperty?: boolean; // if skipped, the model wont be sorted when comparing changes
}

// Used for sortable lists that can also have live updates to the items
// Through some (more or less ugly) hacks it keeps the elements in sync with components
// and rendered in the correct order

export default class NoRerenderSortable<ModelType extends BufferedModelType> {
	ct: JQuery<HTMLElement> | null = null; // is set later
	private elementSelector: string;
	private dataIdProperty: string;
	private dataSortProperty: string;
	private skipSortByDataSortProperty = false;
	private getModel: () => ModelType[];
	private onNodeDelete: (node: JQuery<HTMLElement>) => void;

	@tracked renderedModel: ModelType[] = trackedArray([]); // shown models (not necessarily in the right order)

	constructor(options: Options<ModelType>) {
		this.elementSelector = options.elementSelector;
		this.dataIdProperty = options.dataIdProperty;
		this.dataSortProperty = options.dataSortProperty;
		this.skipSortByDataSortProperty = !!options.skipSortByDataSortProperty;
		this.getModel = options.getModel;
		this.onNodeDelete = options.onNodeDelete;

		this.updateModel(true);
	}

	updateModel(forceSet = false): void {
		const model = Array.from(this.getModel());

		if (forceSet) {
			this.sortAndUpdateRenderedModel(model);
		} else {
			const oldModel = this.renderedModel;
			// comparing ED models here since not all blocks might have ids yet
			const oldModelModels = oldModel.map((item) => item.model),
				newModelModels = model.map((item) => item.model),
				added = model.filter((item: ModelType) => {
					return !oldModelModels.includes(item.model);
				}),
				removed = oldModel.filter((item: ModelType) => {
					return !newModelModels.includes(item.model);
				});

			let mustRerender = false;

			if (removed.length > 0) {
				// first, try to remove the node manually
				mustRerender = removed.some((item: ModelType) => {
					const isDeleted = this.deleteNode(
						Reflect.get(item.model ?? {}, this.dataIdProperty) as number,
					);
					return !isDeleted;
				});

				if (mustRerender) {
					// if not succeeded, do the total rerender hack because ember does not handle moved dom nodes well
					this.renderedModel = trackedArray([]);
					scheduleOnce('afterRender', this, this.updateModel, true);
				} else {
					removed.forEach((item) => {
						const index = this.renderedModel.indexOf(item);
						this.renderedModel.splice(index, 1);
					});
				}
			}

			if (added.length > 0) {
				const newModel = Array.from(this.renderedModel);
				added.forEach((item) => {
					if (!newModel.includes(item)) {
						newModel.push(item);
					}
				});
				this.sortAndUpdateRenderedModel(newModel);
			}
		}
	}

	checkModelNodeOrder(): void {
		const ct = this.ct;
		if (!ct) {
			return;
		}

		const nodes = ct.find(this.elementSelector),
			sortPropertyLowercase = this.dataSortProperty.toLowerCase(),
			correctOrder = this.sortModel(this.getModel()).map(
				(m) => Reflect.get(m, this.dataSortProperty) as number,
			),
			nodesOrder: number[] = [];

		nodes.each((_k: number, node: HTMLElement) => {
			nodesOrder.push(+$(node).attr(`data-${sortPropertyLowercase}`)!);
		});

		if (!equal(correctOrder, nodesOrder)) {
			// sort dom nodes
			const children = nodes.get();

			children.sort((a: HTMLElement, b: HTMLElement) => {
				const aOrder = +$(a).attr(`data-${sortPropertyLowercase}`)!,
					bOrder = +$(b).attr(`data-${sortPropertyLowercase}`)!;
				return aOrder < bOrder ? -1 : aOrder === bOrder ? 0 : 1;
			});

			ct.append(children);
		}
	}

	private sortAndUpdateRenderedModel(models: ModelType[]): void {
		this.renderedModel = trackedArray(this.sortModel(models));
	}

	private sortModel(models: ModelType[]): ModelType[] {
		if (this.skipSortByDataSortProperty) {
			return models;
		} else {
			return sortBy(models, this.dataSortProperty as keyof ModelType);
		}
	}

	private getElementsByDataId(id: number): JQuery | null {
		const els = this.ct?.find(
			`${this.elementSelector}[data-${this.dataIdProperty.toLowerCase()}="${id}"]`,
		);
		return els || null;
	}

	private deleteNode(id: number): boolean {
		let isDeleted = false;

		const nodes = this.getElementsByDataId(id);
		if (nodes) {
			nodes.each((_i: number, node: HTMLElement) => {
				const nodeCt = $(node).closest(this.elementSelector);
				if (nodeCt.length) {
					isDeleted = true;
					this.onNodeDelete(nodeCt);
				}
			});
		}
		return isDeleted;
	}
}
